/**
 * SubmissionsInbox — list of submitted forms for staff review.
 *
 * Features:
 *   - Filter tabs: All / New / Starred / Trash (mirrors the mailbox inbox)
 *   - Row hover action bar: reviewed envelope, star, trash (Restore in Trash)
 *   - Click row → opens SubmissionReview
 *   - Unread count badge (not yet reviewed)
 *   - Flag alerts shown inline
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { forms as formsApi, SubmissionSummary, FormSchema, ghlContactUrl } from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';

interface Props {
  selectedId: string | null;
  onSelect: (submission: SubmissionSummary) => void;
  refreshSignal?: number;
}

type Filter = 'all' | 'new' | 'starred' | 'trash';

/** Rows fetched per page; "Load more" appends the next page (server caps limit at 100). */
const PAGE_SIZE = 25;

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (mins  < 1)  return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export default function SubmissionsInbox({ selectedId, onSelect, refreshSignal }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.locationId ?? '';
  const [items,       setItems]      = useState<SubmissionSummary[]>([]);
  const [total,       setTotal]      = useState(0);
  const [unread,      setUnread]     = useState(0);
  const [schemas,     setSchemas]    = useState<FormSchema[]>([]);
  const [filter,      setFilter]     = useState<Filter>('all');
  const [formFilter,  setFormFilter] = useState<string>('');
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState<string | null>(null);
  // Row currently under the pointer (shows its action bar) + the row whose
  // status action is in flight (disables that row's action buttons).
  const [hoveredId,   setHoveredId]  = useState<string | null>(null);
  const [busyId,      setBusyId]     = useState<string | null>(null);
  const [page,        setPage]       = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // Mirror `page` in a ref so the memoized status-action reload can re-fetch the whole
  // loaded window (not just page 1) without collapsing the list.
  const pageRef = useRef(1);

  // Load form schemas for filter dropdown
  useEffect(() => {
    formsApi.schemas.list().then((res) => {
      if (res.success && res.data) setSchemas(res.data);
    }).catch(() => {/* non-fatal */});
  }, []);

  // silent=true refreshes in the background (after a status action) without the
  // "Loading…" flash replacing the visible list.
  // Fetch page `p`: p===1 REPLACES the list, p>1 APPENDS. `limit` lets the silent
  // status-action reload re-pull the whole loaded window in one shot.
  const load = useCallback(async (opts?: { page?: number; silent?: boolean; limit?: number }) => {
    const p = opts?.page ?? 1;
    const silent = opts?.silent === true;
    const limit = opts?.limit ?? PAGE_SIZE;
    if (!silent && p === 1) setLoading(true);
    setError(null);
    try {
      const res = await formsApi.submissions.list({
        status: filter === 'new' ? 'new' : undefined,
        filter: filter === 'starred' || filter === 'trash' ? filter : undefined,
        formId: formFilter || undefined,
        page: p,
        limit,
      });
      if (res.success && res.data) {
        const rows = res.data.submissions;
        setItems((prev) => (p === 1 ? rows : [...prev, ...rows]));
        setTotal(res.data.total);
        setUnread(res.data.unreadCount);
      } else if (!silent) {
        setError(res.error ?? 'Failed to load submissions');
      }
    } catch {
      if (!silent) setError('Failed to load submissions');
    } finally {
      if (!silent && p === 1) setLoading(false);
    }
  }, [filter, formFilter]);

  // Reset to the first page whenever the filter / form / refresh signal changes.
  useEffect(() => { setPage(1); pageRef.current = 1; void load({ page: 1 }); }, [load, refreshSignal]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    const next = page + 1;
    await load({ page: next });
    setPage(next);
    pageRef.current = next;
    setLoadingMore(false);
  }, [page, load]);

  // Run a status mutation optimistically, then silently reload so the server
  // reconciles the list (a reload can never leave a stale row). A failed call
  // is swallowed — the reload restores truth either way.
  const runStatus = useCallback((mutate: () => Promise<unknown>, id: string) => {
    setBusyId(id);
    void (async () => {
      try { await mutate(); } catch { /* reload reconciles */ }
      // Reconcile the whole loaded window (not just page 1) so "Load more" rows survive.
      finally { setBusyId(null); void load({ page: 1, silent: true, limit: Math.min(100, pageRef.current * PAGE_SIZE) }); }
    })();
  }, [load]);

  const toggleReviewed = (e: React.MouseEvent, sub: SubmissionSummary) => {
    e.stopPropagation();
    const targetReviewed = !sub.reviewed; // currently new → mark reviewed, else new
    setItems((prev) =>
      filter === 'new' && targetReviewed
        ? prev.filter((s) => s.id !== sub.id)
        : prev.map((s) => (s.id === sub.id ? { ...s, reviewed: targetReviewed } : s)),
    );
    if (targetReviewed) setUnread((n) => Math.max(0, n - 1));
    else setUnread((n) => n + 1);
    runStatus(() => formsApi.submissions.setStatus(sub.id, { reviewed: targetReviewed }), sub.id);
  };

  const toggleStar = (e: React.MouseEvent, sub: SubmissionSummary) => {
    e.stopPropagation();
    const next = !sub.starredAt;
    setItems((prev) =>
      filter === 'starred' && !next
        ? prev.filter((s) => s.id !== sub.id)
        : prev.map((s) => (s.id === sub.id ? { ...s, starredAt: next ? new Date().toISOString() : null } : s)),
    );
    runStatus(() => formsApi.submissions.setStatus(sub.id, { starred: next }), sub.id);
  };

  const toggleTrash = (e: React.MouseEvent, sub: SubmissionSummary) => {
    e.stopPropagation();
    const toTrash = filter !== 'trash'; // Trash tab → Restore
    setItems((prev) => prev.filter((s) => s.id !== sub.id));
    runStatus(() => formsApi.submissions.setStatus(sub.id, { deleted: toTrash }), sub.id);
  };

  return (
    <div style={styles.container}>
      {/* Tabs + filter */}
      <div style={styles.controls}>
        <div style={styles.tabs}>
          {(['all', 'new', 'starred', 'trash'] as Filter[]).map((f) => (
            <button
              key={f}
              style={{ ...styles.tab, ...(filter === f ? styles.tabActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'new' ? 'New' : f === 'starred' ? 'Starred' : 'Trash'}
              {f === 'new' && unread > 0 && (
                <span style={styles.badge}>{unread}</span>
              )}
            </button>
          ))}
        </div>
        {schemas.length > 0 && (
          <select
            style={styles.select}
            value={formFilter}
            onChange={(e) => setFormFilter(e.target.value)}
          >
            <option value="">All Forms</option>
            {schemas.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* List */}
      <div style={styles.list}>
        {loading && <p style={styles.status}>Loading…</p>}
        {!loading && error && <p style={{ ...styles.status, color: '#c0392b' }}>{error}</p>}
        {!loading && !error && items.length === 0 && (
          <p style={styles.status}>No submissions found.</p>
        )}
        {items.map((sub) => {
          const isNew      = !sub.reviewed;
          const isStarred  = !!sub.starredAt;
          const inTrash    = filter === 'trash';
          const hasFlags   = sub.aiFlags.length > 0;
          // Owner-first: the stamped identity ('Last, First') wins over the
          // legacy data_enc preview heuristic (only populated for pre-feature rows).
          const owner      = sub.submitter;
          const name       = owner && (owner.firstName || owner.lastName)
            ? [owner.lastName, owner.firstName].filter(Boolean).join(', ')
            : [sub.preview.firstName, sub.preview.lastName].filter(Boolean).join(' ') || sub.contactGhlId?.slice(0, 12) || 'Unknown';
          const dob        = owner?.dob ?? sub.preview.dob;
          const attachCount = sub.attachmentCount ?? 0;
          const isSelected = sub.id === selectedId;
          const showActions = hoveredId === sub.id;
          const rowBusy    = busyId === sub.id;

          return (
            <div
              key={sub.id}
              style={{ ...styles.row, ...(isSelected ? styles.rowSelected : {}) }}
              onMouseEnter={() => setHoveredId(sub.id)}
              onMouseLeave={() => setHoveredId((h) => (h === sub.id ? null : h))}
            >
              <button style={styles.item} onClick={() => onSelect(sub)}>
                <div style={styles.rowTop}>
                  <span style={{ ...styles.name, fontWeight: isNew ? 700 : 400 }}>
                    {sub.contactGhlId ? (
                      <a
                        href={ghlContactUrl(locationId, sub.contactGhlId)}
                        target="_blank"
                        rel="noreferrer"
                        style={styles.contactLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {name}
                      </a>
                    ) : name}
                    {dob && <span style={styles.dobSuffix}> · DOB {dob}</span>}
                  </span>
                  <span style={styles.time}>{relativeTime(sub.submittedAt)}</span>
                </div>
                <div style={styles.rowMid}>
                  <span style={styles.formName}>{sub.formName}</span>
                  {attachCount > 0 && (
                    <span
                      style={styles.attachChip}
                      title={`${attachCount} attachment${attachCount === 1 ? '' : 's'}`}
                      aria-label={`${attachCount} attachment${attachCount === 1 ? '' : 's'}`}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      {attachCount}
                    </span>
                  )}
                  {hasFlags && <span style={styles.flagBadge}>⚠ Flagged</span>}
                  {isNew ? (
                    <span style={styles.newBadge}>New</span>
                  ) : (
                    <span style={styles.reviewedBadge}>Reviewed</span>
                  )}
                </div>
                {sub.matchConfidence !== null && sub.matchReviewStatus === 'pending' && (
                  <div style={styles.matchNote}>Contact match needs review</div>
                )}
              </button>

              {showActions && (
                <div style={styles.actionBar}>
                  {!inTrash && (
                    <>
                      <button
                        style={{ ...styles.actionBtn, ...(isStarred ? styles.actionBtnStar : {}) }}
                        onClick={(e) => toggleStar(e, sub)}
                        disabled={rowBusy}
                        title={isStarred ? 'Unstar' : 'Star'}
                        aria-label={isStarred ? 'Unstar' : 'Star'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </button>
                      <button
                        style={{ ...styles.actionBtn, ...(isNew ? styles.actionBtnUnread : {}) }}
                        onClick={(e) => toggleReviewed(e, sub)}
                        disabled={rowBusy}
                        title={isNew ? 'Mark as reviewed' : 'Mark as new'}
                        aria-label={isNew ? 'Mark as reviewed' : 'Mark as new'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                      </button>
                    </>
                  )}
                  <button
                    style={styles.actionBtn}
                    onClick={(e) => toggleTrash(e, sub)}
                    disabled={rowBusy}
                    title={inTrash ? 'Restore' : 'Trash'}
                    aria-label={inTrash ? 'Restore' : 'Trash'}
                  >
                    {inTrash ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                        <path d="M3 3v5h5" />
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 6h18" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" x2="10" y1="11" y2="17" />
                        <line x1="14" x2="14" y1="11" y2="17" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {!loading && !error && total > items.length && (
          <button style={styles.loadMore} onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Load more (${items.length} of ${total})`}
          </button>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', borderRight: '1px solid #e5e7eb' },
  controls:  { padding: '8px 12px', borderBottom: '1px solid #e5e7eb' },
  tabs:      { display: 'flex', marginBottom: 8 },
  tab: {
    flex: 1, padding: '6px 4px', border: 'none', background: 'none',
    cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#6b7280',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    // Every tab reserves the 2px underline (transparent) so the active tab toggles it to
    // blue with NO layout shift and the active state reads unambiguously.
    borderBottom: '2px solid transparent',
  },
  tabActive: { color: '#2563eb', borderBottom: '2px solid #2563eb' },
  loadMore: {
    display: 'block', width: 'calc(100% - 24px)', margin: '8px 12px 16px',
    padding: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151',
  },
  badge: {
    background: '#ef4444', color: '#fff', borderRadius: '999px',
    fontSize: 10, fontWeight: 700, padding: '1px 5px',
  },
  select: {
    width: '100%', padding: '5px 8px', border: '1px solid #d1d5db',
    borderRadius: 4, fontSize: 12, color: '#374151',
  },
  list:   { flex: 1, overflowY: 'auto' },
  status: { padding: '20px 16px', color: '#9ca3af', fontSize: 13, textAlign: 'center' },
  row: {
    position: 'relative', borderBottom: '1px solid #f3f4f6',
  },
  item: {
    display: 'block', width: '100%', padding: '10px 14px',
    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
  },
  rowSelected: { background: '#eff6ff' },
  actionBar: {
    position: 'absolute', top: 8, right: 10,
    display: 'flex', gap: 2, background: '#fff',
    border: '1px solid #e5e7eb', borderRadius: 6, padding: 2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  actionBtn: {
    background: 'transparent', border: 'none', borderRadius: 4, padding: 4,
    cursor: 'pointer', color: '#6b7280',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0,
  },
  actionBtnStar:   { color: '#f59e0b' },
  actionBtnUnread: { color: '#2563eb' },
  rowTop:      { display: 'flex', justifyContent: 'space-between', marginBottom: 4 },
  name:        { fontSize: 14, color: '#111827' },
  dobSuffix:   { fontSize: 12, color: '#9ca3af', fontWeight: 400 },
  time:        { fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' },
  rowMid:      { display: 'flex', alignItems: 'center', gap: 6 },
  formName:    { fontSize: 12, color: '#6b7280', flex: 1 },
  attachChip:  { display: 'inline-flex', alignItems: 'center', gap: 3, background: '#f3f4f6', color: '#4b5563', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, flexShrink: 0 },
  flagBadge:   { background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4 },
  newBadge:    { background: '#dbeafe', color: '#2563eb', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4 },
  reviewedBadge: { background: '#f0fdf4', color: '#15803d', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4 },
  matchNote:   { fontSize: 11, color: '#d97706', marginTop: 2 },
  contactLink: { color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' as const },
};
